import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, fmtMoney } from "@/lib/api";
import { usePageMeta, useProductSchema } from "@/lib/seo";
import { STORE_ADDRESS } from "@/constants/store";
import { useCart } from "@/context/CartContext";
import { ChevronLeft, ChevronRight, X, Plus, Minus, ShoppingCart, Truck, Store, Tag, CheckCircle2, MessageCircle, Play } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import PalletManifest from "@/components/shop/PalletManifest";
import VideoPlayerModal from "@/components/shop/VideoPlayerModal";
import ProductReviews from "@/components/shop/ProductReviews";
import { Stars } from "@/components/shop/ProductReviews";
import { PalletPhotoGallery } from "@/components/shop/PalletPhotoGallery";
import { DepartmentPolicy } from "@/components/policy/DepartmentPolicy";
import { AvailabilityBadge } from "@/components/shop/AvailabilityBadge";
import { AgeGate, hasAdultConsent, hasTobaccoConsent } from "@/components/AgeGate";
import { TestRideModal } from "@/components/TestRideModal";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [adultOk, setAdultOk] = useState(hasAdultConsent());
  const [tobaccoOk, setTobaccoOk] = useState(hasTobaccoConsent());
  const [showTestRide, setShowTestRide] = useState(false);
  const [swapWant, setSwapWant] = useState(false);
  const [swapText, setSwapText] = useState("");
  const [reviewStats, setReviewStats] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const touchX = useRef(null);

  const onImgTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onImgTouchEnd = (e) => {
    if (touchX.current == null || images.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) setActiveImg((activeImg + (dx < 0 ? 1 : -1) + images.length) % images.length);
    touchX.current = null;
  };
  const { addItem } = useCart();
  const navigate = useNavigate();

  const loadReviews = (pid) =>
    api.get(`/reviews/product/${pid}`).then((r) => setReviewStats(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
      const firstAvail = (r.data.colors || []).find((c) => c.available !== false);
      setSelectedColor(firstAvail ? firstAvail.name : null);
      loadReviews(r.data.id);
    }).catch(() => navigate("/shop"));
  }, [slug, navigate]);

  usePageMeta(
    product ? product.name : "Product",
    product
      ? `${product.name.slice(0, 70)} — ${product.stock > 0 || product.fulfillment_type === "order_in" ? "in stock" : "sold out"} at $${product.price} from IPVALUE Marketplace, Grand Rapids MI. ${product.ships_only ? "Ships straight to your door by carrier." : "Free same-day store pickup or local delivery."}`.slice(0, 160)
      : undefined,
    product
      ? {
          canonicalPath: `/product/${product.slug}`,
          image: (product.images || [])[0]
            ? (product.images[0].startsWith("http") ? product.images[0] : `https://ipvaluemarketplace.com/api/files/${product.images[0]}`)
            : undefined,
        }
      : {}
  );
  useProductSchema(product, reviewStats);

  if (!product) return <div className="p-20 text-center ipv-eyebrow">Loading…</div>;

  const images = product.images?.length ? product.images : [null];
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const pctOff = onSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;
  const outOfStock = product.fulfillment_type !== "order_in" && product.stock <= 0;
  const isSpecial = product.fulfillment_type === "order_in";
  const leadDays = product.lead_time_days || 7;
  const colors = product.colors || [];
  const deal = product.bulk_deal;
  const isManifestedPallet = !!product.is_pallet && (product.manifest_items?.length > 0);
  const swapRequest = swapWant && swapText.trim() ? swapText.trim() : null;

  const handleAdd = () => {
    if (colors.length && !selectedColor) {
      toast.error("Please choose a color first");
      return;
    }
    if (swapWant && !swapText.trim()) {
      toast.error("Tell us which item to swap and what you'd like instead");
      return;
    }
    addItem(product, qty, selectedColor, swapRequest);
    toast.success(`Added ${qty} × ${product.name}${selectedColor ? ` (${selectedColor})` : ""} to cart${swapRequest ? " — swap request noted (+$99)" : ""}`);
  };

  const handleBuyNow = () => {
    if (colors.length && !selectedColor) {
      toast.error("Please choose a color first");
      return;
    }
    if (swapWant && !swapText.trim()) {
      toast.error("Tell us which item to swap and what you'd like instead");
      return;
    }
    addItem(product, qty, selectedColor, swapRequest);
    navigate("/cart");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-10" data-testid="product-page">
      {(product.category === "Adult (18+)" && !adultOk) || (product.category === "Tobacco Accessories" && !tobaccoOk) ? (
        <AgeGate minAge={product.category === "Tobacco Accessories" ? 21 : 18} onConfirm={() => (product.category === "Tobacco Accessories" ? setTobaccoOk(true) : setAdultOk(true))} />
      ) : (
      <>
      {showTestRide && <TestRideModal product={product} onClose={() => setShowTestRide(false)} />}
      <Link to="/shop" className="inline-flex items-center text-sm text-gray-600 hover:text-black gap-1 mb-8" data-testid="back-to-shop">
        <ChevronLeft className="w-4 h-4" /> Back to shop
      </Link>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square border border-gray-200 rounded-lg bg-[#F4F4F5] overflow-hidden">
            {images[activeImg] ? (
              <button type="button" className="w-full h-full cursor-zoom-in" onClick={() => setLightbox(true)} onTouchStart={onImgTouchStart} onTouchEnd={onImgTouchEnd} data-testid="pdp-main-image-btn" aria-label="View full-size image">
                <img src={fileUrl(images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
            )}
          </div>
          {/* Video slot — plays the owner's MP4 promo in a popup player */}
          {product.video_url && (
            <button
              type="button"
              onClick={() => setShowVideo(true)}
              className="mt-2 w-full relative rounded-lg overflow-hidden border-2 border-[#F0B90B]/60 hover:border-[#F0B90B] group/vid transition-colors"
              data-testid="pdp-video-tile"
              aria-label="Watch product video"
            >
              <div className="aspect-[16/6] sm:aspect-[16/5] bg-[#0A1E5E] relative">
                {images[0] && (
                  <img src={fileUrl(images[0])} alt="" className="w-full h-full object-cover opacity-40" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-[#071330]/90 to-[#0A1E5E]/40" />
                <div className="absolute inset-0 flex items-center justify-start gap-4 px-6">
                  <span className="w-12 h-12 shrink-0 rounded-full bg-[#F0B90B] text-[#0A1E5E] flex items-center justify-center shadow-[0_0_20px_rgba(240,185,11,0.55)] group-hover/vid:scale-110 transition-transform">
                    <Play className="w-5 h-5 fill-current ml-0.5" />
                  </span>
                  <span className="text-left">
                    <span className="block font-display font-black text-white text-base sm:text-lg leading-tight">Watch the product video</span>
                    <span className="block text-[#F0B90B] text-xs font-bold tracking-wide uppercase mt-0.5">Tap to play with sound</span>
                  </span>
                </div>
              </div>
            </button>
          )}
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-2">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square border rounded-lg bg-[#F4F4F5] overflow-hidden ${
                    activeImg === i ? "border-[#0A1C40] border-2" : "border-gray-200"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  {img && <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/shop/${product.category}`} className="ipv-eyebrow text-[#0A1C40] hover:underline">
              {product.category}
            </Link>
            {product.gold_tag && (
              <Badge className="bg-[#FFC220] hover:bg-[#FFC220] text-[#0A1C40] rounded-sm text-[10px] tracking-widest font-black uppercase border border-[#C9971E]" data-testid="pdp-gold-deal-badge">
                ★ GOLD DEAL
              </Badge>
            )}
            {product.is_pallet && (
              <Badge className="bg-[#DC2626] text-white rounded-sm text-[10px] tracking-widest font-bold uppercase">
                Wholesale Pallet
              </Badge>
            )}
            {onSale && (
              <Badge className="bg-[#0A1C40] text-white rounded-sm text-[10px] tracking-widest font-bold uppercase">
                On Sale
              </Badge>
            )}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4 leading-tight" data-testid="product-name">
            {product.name}
          </h1>
          <div className="mt-3">
            <AvailabilityBadge product={product} size="lg" />
          </div>
          {reviewStats?.count > 0 && (
            <a href="#reviews" className="mt-2 inline-flex items-center gap-1.5 text-sm" data-testid="pdp-rating-summary">
              <Stars value={reviewStats.average} />
              <span className="font-bold">{reviewStats.average}</span>
              <span className="text-gray-500 underline">({reviewStats.count} review{reviewStats.count > 1 ? "s" : ""})</span>
            </a>
          )}

          <div className="mt-6">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="font-display font-black text-4xl" data-testid="product-price">
                {onSale && <span className="text-lg font-bold text-[#DC2626] mr-1.5">Now</span>}
                {fmtMoney(product.price)}
              </div>
              {onSale && (
                <>
                  <div className={product.gold_tag ? "text-2xl font-bold text-[#B45309] line-through" : "text-xl text-gray-400 line-through"} data-testid="pdp-compare-price">
                    {fmtMoney(product.compare_at_price)}
                  </div>
                  <div className="bg-[#DC2626] text-white text-sm font-black px-2.5 py-0.5 rounded-full" data-testid="pct-off-badge">
                    -{pctOff}%
                  </div>
                </>
              )}
            </div>
            {onSale && (
              <div className={`mt-1.5 text-sm font-bold ${product.gold_tag ? "text-[#B45309]" : "text-green-700"}`} data-testid="you-save-msg">
                {product.gold_tag
                  ? `★ GOLD DEAL — sells elsewhere for ${fmtMoney(product.compare_at_price)} · you save ${fmtMoney(product.compare_at_price - product.price)}`
                  : `You save ${fmtMoney(product.compare_at_price - product.price)}`}
              </div>
            )}
          </div>

          {deal && deal.min_qty >= 2 && deal.percent_off > 0 && (
            <div className="mt-4 border-2 border-dashed border-[#FFC220] bg-amber-50 rounded-lg px-4 py-3 inline-flex items-center gap-2" data-testid="bulk-deal-banner">
              <Tag className="w-4 h-4 text-[#C99700]" />
              <span className="font-bold text-sm">
                DEAL: Buy {deal.min_qty}+ and save {deal.percent_off}% — applied automatically at checkout
              </span>
            </div>
          )}

          {product.sku && (
            <div className="ipv-eyebrow mt-4 text-gray-500">
              SKU · {product.sku}
            </div>
          )}

          <p className="mt-8 text-gray-700 leading-relaxed whitespace-pre-line" data-testid="product-description">
            {product.description || "No description provided."}
          </p>

          {colors.length > 0 && (
            <div className="mt-8 border-t border-gray-200 pt-6" data-testid="color-selector">
              <div className="ipv-eyebrow mb-3">
                Color{selectedColor ? <span className="text-black font-bold"> · {selectedColor}</span> : ""}
              </div>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => {
                  const unavailable = c.available === false;
                  const active = selectedColor === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      disabled={unavailable}
                      onClick={() => setSelectedColor(c.name)}
                      className={`px-4 py-2 border-2 rounded-full text-sm font-bold select-none [touch-action:manipulation] ${
                        unavailable
                          ? "border-gray-200 text-gray-300 line-through cursor-not-allowed bg-gray-50"
                          : active
                          ? "border-[#0A1C40] bg-[#0A1C40] text-white"
                          : "border-gray-300 text-black"
                      }`}
                      data-testid={`color-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {c.name}
                      {unavailable && <span className="ml-1 no-underline text-[10px] uppercase"> · Not available</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 border-t border-gray-200 pt-6" data-testid="fulfillment-selector">
            <div className="font-display font-bold text-lg mb-3">How do you want your item?</div>
            {outOfStock ? (
              <div className="text-[#DC2626] font-bold" data-testid="oos-msg">Out of stock</div>
            ) : (
              <>
                {!product.ships_only && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="border-2 border-[#0A1C40] rounded-lg p-4 relative" data-testid="fulfillment-tile-pickup">
                    <CheckCircle2 className="w-4 h-4 text-[#16A34A] absolute top-3 right-3" />
                    <Store className="w-6 h-6 text-[#0A1C40]" />
                    <div className="font-bold mt-2 text-sm">Free pickup</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {isSpecial ? `Ready in ~${leadDays} days` : "Ready during store hours"} · {STORE_ADDRESS.short}
                    </div>
                  </div>
                  <div className="border-2 border-[#0A1C40] rounded-lg p-4 relative" data-testid="fulfillment-tile-delivery">
                    <CheckCircle2 className="w-4 h-4 text-[#16A34A] absolute top-3 right-3" />
                    <Truck className="w-6 h-6 text-[#0A1C40]" />
                    <div className="font-bold mt-2 text-sm">Local delivery</div>
                    <div className="text-xs text-gray-600 mt-0.5">FREE on orders $299+ within 10 miles · quoted at checkout</div>
                  </div>
                </div>
                )}
                {isSpecial ? (
                  <div className="mt-3 bg-blue-50 border border-[#0A1C40]/20 rounded-lg px-4 py-3" data-testid="special-order-msg">
                    <div className="font-bold text-[#0A1C40] text-sm">Special order — ready in ~{leadDays} days</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Order today, we bring it in for you. Pick it up in store or have it delivered when it arrives — we'll keep you updated every step.
                    </div>
                  </div>
                ) : product.ships_only ? (
                  <div className="mt-3 bg-[#0A1C40] text-white rounded-lg px-4 py-3" data-testid="ships-to-you-msg">
                    <div className="font-bold text-[#FFC220] text-sm">Ships to you</div>
                    <div className="text-xs text-white/80 mt-1">
                      {product.ship_eta_text || "Estimated US delivery: 3–7 business days"} · standard carrier shipping only — store pickup and local delivery don't apply to this item.
                    </div>
                    <div className="text-[11px] text-white/60 mt-1.5" data-testid="ships-return-model">
                      Returns: by mail — refund to your original payment method. No store credit on shipped items.
                    </div>
                  </div>
                ) : (
                  <div className="mt-3" data-testid="stock-msg">
                    <div className="text-green-700 font-bold text-sm">Available in store now</div>
                    <div className="text-xs text-gray-700 mt-0.5" data-testid="pickup-line">
                      Pickup in store: {product.stock > 0 ? "Available" : "Not available"}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1" data-testid="instore-return-model">
                      Returns: 14-day exchange or store credit (receipt required)
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {!outOfStock && (
            <div className="mt-6 flex items-center gap-4">
              <div className="ipv-eyebrow">Qty</div>
              <div className="flex items-center border border-gray-300">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-gray-100" data-testid="qty-decr">
                  <Minus className="w-4 h-4" />
                </button>
                <div className="w-12 text-center font-bold" data-testid="qty-display">{qty}</div>
                <button onClick={() => setQty(isSpecial ? qty + 1 : Math.min(product.stock, qty + 1))} className="p-2.5 hover:bg-gray-100" data-testid="qty-incr">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Addendum A, Option A — manifested pallet item swap (+$99/order) */}
          {isManifestedPallet && !outOfStock && (
            <div className={`mt-6 border-2 p-4 ${swapWant ? "border-[#C9971E] bg-[#FFC220]/10" : "border-gray-200"}`} data-testid="pallet-swap-box">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={swapWant}
                  onChange={(e) => setSwapWant(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#C9971E] shrink-0"
                  data-testid="pallet-swap-checkbox"
                />
                <span className="text-sm leading-relaxed">
                  <b>Want one item swapped?</b> Request it for a <b>$99 swap fee</b> (one fee per order) — otherwise
                  you take the pallet exactly as built.
                </span>
              </label>
              {swapWant && (
                <textarea
                  className="input-base mt-3"
                  rows={2}
                  placeholder='Example: "Swap the office chair for a recliner"'
                  value={swapText}
                  onChange={(e) => setSwapText(e.target.value)}
                  data-testid="pallet-swap-text"
                />
              )}
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            {(product.category === "Adult (18+)" || product.category === "Tobacco Accessories") ? (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/shopify/checkout-link/${product.id}`, { params: { qty } });
                    window.location.href = data.url;
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Online checkout unavailable — call (616) 410-0158");
                  }
                }}
                className="btn-gold inline-flex items-center justify-center gap-2 flex-1"
                data-testid="shopify-checkout-btn"
              >
                🔒 Checkout securely — discreet billing &amp; packaging
              </button>
            ) : (
            <>
            <button
              onClick={handleAdd}
              disabled={outOfStock}
              className="btn-outline inline-flex items-center justify-center gap-2 flex-1"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="w-4 h-4" /> Add to cart
            </button>
            <button
              onClick={handleBuyNow}
              disabled={outOfStock}
              className="btn-gold inline-flex items-center justify-center gap-2 flex-1"
              data-testid="buy-now-btn"
            >
              Buy now
            </button>
            </>
            )}
          </div>

          {/* Text-to-Buy — opens the shopper's SMS app pre-filled with this product */}
          <a
            href={`sms:+16164100158?&body=${encodeURIComponent(`Hi IPVALUE! I have a question about: ${product.name} — https://ipvaluemarketplace.com/product/${product.slug || product.id}`)}`}
            className="mt-3 w-full border-2 border-gray-300 hover:border-[#0A1C40] text-gray-700 hover:text-[#0A1C40] font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2 [touch-action:manipulation]"
            data-testid="text-us-btn"
          >
            <MessageCircle className="w-4 h-4" /> Text us about this item — (616) 410-0158
          </a>
          <a
            href="tel:6164100158"
            className="mt-2 w-full bg-gray-100 hover:bg-[#0A1C40] hover:text-[#FFC220] text-[#0A1C40] font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2 transition-colors [touch-action:manipulation]"
            data-testid="pdp-call-link"
          >
            📞 Questions? Call us: (616) 410-0158
          </a>

          {product.category === "E-Bikes" && (
            <button
              onClick={() => setShowTestRide(true)}
              className="mt-3 w-full bg-[#0A1C40] text-[#FFC220] font-black py-3 rounded-full text-sm flex items-center justify-center gap-2 hover:bg-black transition-colors [touch-action:manipulation]"
              data-testid="testride-open-btn"
            >
              🚲 Book a FREE in-store test ride
            </button>
          )}
        </div>
      </div>

      {/* Mobile sticky buy bar — keeps price + CTA in reach while scrolling */}
      {!outOfStock && product.category !== "Adult (18+)" && product.category !== "Tobacco Accessories" && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 border-[#0A1C40] px-4 py-3 flex items-center gap-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]" data-testid="mobile-buy-bar">
          <div className="min-w-0">
            <div className="font-display font-black text-xl leading-none">{fmtMoney(product.price)}</div>
            {product.compare_at_price > product.price && (
              <div className="text-[11px] text-gray-400 line-through">{fmtMoney(product.compare_at_price)}</div>
            )}
          </div>
          <button
            onClick={handleAdd}
            className="flex-1 border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-3 rounded-full text-sm [touch-action:manipulation]"
            data-testid="mobile-add-to-cart"
          >
            Add to cart
          </button>
          <button
            onClick={handleBuyNow}
            className="cta-sweep cta-pulse flex-1 bg-[#FFC220] text-[#0A1C40] font-black py-3 rounded-full text-sm [touch-action:manipulation]"
            data-testid="mobile-buy-now"
          >
            Buy now
          </button>
        </div>
      )}
      {!outOfStock && <div className="h-20 lg:hidden" />}

      {/* Unmanifested pallets — real-photo gallery builds buyer confidence */}
      {product.is_pallet && !(product.manifest_items?.length) && (
        <PalletPhotoGallery product={product} />
      )}

      {/* Pallet manifest — only renders when this product has manifest_items */}
      {product.is_pallet && (
        <PalletManifest productId={product.id} />
      )}

      {/* Customer reviews + Review schema — powers star snippets in Google */}
      <ProductReviews product={product} stats={reviewStats} onRefresh={() => loadReviews(product.id)} />

      {/* Full-size image lightbox with swipeable gallery */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" data-testid="pdp-image-lightbox" onClick={() => setLightbox(false)}>
          <button type="button" className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/25 rounded-full p-2.5 text-white transition-colors" onClick={() => setLightbox(false)} data-testid="lightbox-close" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
          <div className="flex-1 flex items-center justify-center px-4 md:px-16 min-h-0" onClick={(e) => e.stopPropagation()} onTouchStart={onImgTouchStart} onTouchEnd={onImgTouchEnd}>
            {images.length > 1 && (
              <button type="button" className="shrink-0 bg-white/10 hover:bg-white/25 rounded-full p-2.5 text-white transition-colors mr-2" onClick={() => setActiveImg((activeImg - 1 + images.length) % images.length)} data-testid="lightbox-prev" aria-label="Previous image">
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <img src={fileUrl(images[activeImg])} alt={product.name} className="max-h-[78vh] max-w-full object-contain select-none" data-testid="lightbox-image" />
            {images.length > 1 && (
              <button type="button" className="shrink-0 bg-white/10 hover:bg-white/25 rounded-full p-2.5 text-white transition-colors ml-2" onClick={() => setActiveImg((activeImg + 1) % images.length)} data-testid="lightbox-next" aria-label="Next image">
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 justify-center overflow-x-auto px-4 py-4" onClick={(e) => e.stopPropagation()}>
              {images.map((img, i) => (
                <button key={i} type="button" onClick={() => setActiveImg(i)} className={`w-16 h-16 shrink-0 rounded overflow-hidden border-2 ${activeImg === i ? "border-[#FFC220]" : "border-white/20"}`} data-testid={`lightbox-thumb-${i}`}>
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Department policy block — spec 8B: visible on every product page */}
      <DepartmentPolicy department={product.category} />
      {showVideo && product.video_url && (
        <VideoPlayerModal
          src={product.video_url}
          title={product.name}
          poster={images[0] ? fileUrl(images[0]) : undefined}
          onClose={() => setShowVideo(false)}
        />
      )}
      </>
      )}
    </div>
  );
}
